import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/* Every screen wraps its table in a different amount of chrome - a filter bar
 * above, a purchase button or pagination below - so a fixed `scroll.y` (or a
 * hand-tuned `calc(100vh - Npx)`) is wrong on all of them at once. Instead we
 * measure what the viewport has left underneath the table body and hand that
 * to antd, so the table always runs to the bottom of the screen and only its
 * rows scroll. */

// Below this the table is more cramped than a scrolling page would be, so we
// stop shrinking it and let the page scroll instead.
const MIN_HEIGHT = 240;
// Re-measuring is cheap, but re-rendering on sub-pixel layout noise is not.
const THRESHOLD = 2;

const px = (value) => parseFloat(value) || 0;

const isInFlow = (el) => {
  const { position, display } = window.getComputedStyle(el);
  return display !== 'none' && position !== 'fixed' && position !== 'absolute';
};

const takesSpace = (el) => isInFlow(el) && el.getBoundingClientRect().height > 0;

/* Everything between the bottom of the table body and the bottom of the page:
 * the pagination inside the table wrapper, then, walking up to <body>, each
 * ancestor's trailing siblings, margins, paddings and borders. All of it is
 * independent of the table's own height, which is what keeps the measurement
 * from feeding back into itself. */
const spaceBelow = (container, bodyBottom, watched) => {
  let reserved = container.getBoundingClientRect().bottom - bodyBottom;

  for (let node = container; node && node !== document.body; node = node.parentElement) {
    /* Measured as one span, from this node's bottom edge to the bottom of the
       last sibling that still takes space, so margins collapsing between them
       are counted once rather than from both sides. */
    let last = null;
    for (let sibling = node.nextElementSibling; sibling; sibling = sibling.nextElementSibling) {
      watched.add(sibling);
      if (takesSpace(sibling)) last = sibling;
    }
    if (last) {
      reserved += last.getBoundingClientRect().bottom - node.getBoundingClientRect().bottom;
      reserved += px(window.getComputedStyle(last).marginBottom);
    } else {
      reserved += px(window.getComputedStyle(node).marginBottom);
    }
    /* Anything above the table moves its top edge rather than the space under
       it, so it is watched but not counted. */
    for (let sibling = node.previousElementSibling; sibling; sibling = sibling.previousElementSibling) {
      watched.add(sibling);
    }
    const parent = node.parentElement;
    if (parent) {
      const { paddingBottom, borderBottomWidth } = window.getComputedStyle(parent);
      reserved += px(paddingBottom) + px(borderBottomWidth);
    }
  }

  return reserved;
};

/**
 * Returns the height the antd table body may take, or `null` while it cannot be
 * measured (before mount, or on the mobile list view where there is no table).
 *
 * @param {object} containerRef ref on the element wrapping the antd table
 * @param {boolean} enabled     false on layouts that should keep their natural height
 */
const useTableBodyHeight = (containerRef, enabled = true) => {
  const [height, setHeight] = useState(null);
  const heightRef = useRef(null);
  const observerRef = useRef(null);
  const watchedRef = useRef(new Set());

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!enabled || !container) return;

    const body = container.querySelector('.ant-table-body');
    if (!body) return;

    const rect = body.getBoundingClientRect();
    // Document-relative top, so a scrolled page measures the same as one at rest.
    const top = rect.top + window.pageYOffset;
    const watched = new Set();
    const available = Math.round(window.innerHeight - top - spaceBelow(container, rect.bottom, watched));
    const next = Math.max(available, MIN_HEIGHT);

    /* Follow whatever this measurement depended on. Those elements are laid out
       independently of the table, so re-measuring when one of them resizes
       settles rather than oscillates - and a toolbar or a checkout bar that
       reflows after the web font loads no longer leaves a stale height behind. */
    const observer = observerRef.current;
    if (observer) {
      watchedRef.current.forEach((el) => !watched.has(el) && observer.unobserve(el));
      watched.forEach((el) => !watchedRef.current.has(el) && observer.observe(el));
      watchedRef.current = watched;
    }

    if (heightRef.current === null || Math.abs(next - heightRef.current) > THRESHOLD) {
      heightRef.current = next;
      setHeight(next);
    }
  }, [containerRef, enabled]);

  // After every render: the rows, the toolbar above and the buttons below can
  // all change height as data arrives.
  useLayoutEffect(measure);

  useEffect(() => {
    if (window.ResizeObserver) observerRef.current = new window.ResizeObserver(measure);

    // Late layout shifts (web fonts, logos loading in the header) move the
    // table down after the first paint.
    const raf = window.requestAnimationFrame(measure);
    if (document.fonts) document.fonts.ready.then(measure);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = null;
      watchedRef.current = new Set();
    };
  }, [measure]);

  return enabled ? height : null;
};

export default useTableBodyHeight;
